/** Lifecycle of an archived record. */
export type ArchiveStatus = 'archived' | 'restored';

export const ARCHIVE_STATUSES: ArchiveStatus[] = ['archived', 'restored'];

/** User that deleted or restored the record, as captured at that time. */
export interface ArchiveActor {
    id: string | null;
    name?: string;
    email?: string;
    role?: string;
}

export interface ArchiveRecord {
    id: string;
    /** Model the record belonged to, e.g. "SellInvoice". */
    collectionName: string;
    /** Id the record had before deletion; a restore puts it back under it. */
    documentId: string;
    /** Invoice number / name / code, so a row is readable at a glance. */
    label?: string;
    status: ArchiveStatus;
    deletedAt: string;
    deletedBy: ArchiveActor;
    restoredAt?: string | null;
    restoredBy?: ArchiveActor | null;
    /** Full snapshot — only returned by the details endpoint. */
    data?: any;
    createdAt?: string;
    updatedAt?: string;
}

export interface ArchivePagination {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface ArchiveListResult {
    data: ArchiveRecord[];
    pagination: ArchivePagination;
}

/** Per-model counts, used to build the model filter options. */
export interface ArchiveSummaryItem {
    collectionName: string;
    total: number;
    archived: number;
    restored: number;
    lastDeletedAt: string | null;
}

export interface ArchiveSummaryResult {
    summary: ArchiveSummaryItem[];
    /** Models the API is able to restore into; the rest are read-only. */
    restorableModels: string[];
}

export interface ArchiveQuery {
    /** One or more model names, e.g. ["SellInvoice", "Customer"]. */
    collectionName?: string[];
    status?: ArchiveStatus[];
    documentId?: string;
    userId?: string;
    search?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    sort?: 'asc' | 'desc';
    includeData?: boolean;
}

/** One row of a bulk restore result. */
export interface RestoreOutcome {
    id: string;
    restored: boolean;
    record?: ArchiveRecord;
    message?: string;
}

export interface RestoreManyResult {
    results: RestoreOutcome[];
    restored: number;
    failed: number;
}

export const emptyArchivePagination: ArchivePagination = {
    total: 0,
    page: 1,
    limit: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
};
